import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import PartnerProgrammesPanel from '../components/admin/PartnerProgrammesPanel';
import { toast } from 'sonner';
import { 
    Plus, Search, Building2, Palette, Edit2, Trash2, 
    Mail, Phone, User, Star, AlertCircle, CheckCircle2, Send, Loader2,
    Sparkles, Gift
} from 'lucide-react';
import axios from 'axios';

import { API_BASE } from '../lib/apiBase';
const API = API_BASE;

// Central Partner admin — three tabs:
//   1. Production Partners (Sony / Supplier / Repro — unchanged)
//   2. Affinity Programmes (formerly "Partner Registration" in
//      System Settings; the underlying DB collection is still
//      ``partner_programmes`` and every /register/{slug} URL, discount
//      calc and API route is unchanged. Only the UI terminology is
//      Affinity.)
//   3. Affiliate Programme (placeholder for a future revenue-share /
//      referral programme; kept visible so admins see the roadmap)
export default function PartnersPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || 'production';
    const setActiveTab = (t) => {
        const next = new URLSearchParams(searchParams);
        if (t === 'production') next.delete('tab'); else next.set('tab', t);
        setSearchParams(next, { replace: true });
    };
    const { user } = useAuth();
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [activeFilter, setActiveFilter] = useState('all');
    const [resendingInvite, setResendingInvite] = useState(null);
    
    // Dialog states
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        type: 'Repro',
        is_active: true,
        contact_name: '',
        phone: '',
        email_to: '',
        email_cc: '',
        email_reply_to: '',
        rfq_email: '',
        rfq_email_cc: '',
        notes: '',
        default_currency: 'EUR',
        is_default: false,
        // Stable supplier resolution fields (Suppliers only). Free-text
        // canonical id + comma-separated alternative display names.
        supplier_code: '',
        aliases: '',
        // Repro-partner vacation windows. Each row = { id?, start_date,
        // end_date, fallback_partner_id, note }. Persisted as-is; the
        // server assigns UUIDs to new rows.
        unavailable_periods: []
    });
    
    const [partnerTypes, setPartnerTypes] = useState([]);
    const [currencies, setCurrencies] = useState([]);

    const fetchPartners = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (typeFilter !== 'all') params.append('type', typeFilter);
            if (activeFilter !== 'all') params.append('is_active', activeFilter === 'active');
            if (searchQuery) params.append('search', searchQuery);
            
            const response = await axios.get(`${API}/api/partners?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPartners(response.data);
        } catch (error) {
            toast.error('Failed to load partners');
        } finally {
            setLoading(false);
        }
    }, [typeFilter, activeFilter, searchQuery]);

    const fetchMetadata = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const [typesRes, currenciesRes] = await Promise.all([
                axios.get(`${API}/api/metadata/partner-types`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API}/api/metadata/partner-currencies`, { headers: { Authorization: `Bearer ${token}` } })
            ]);
            setPartnerTypes(typesRes.data.types);
            setCurrencies(currenciesRes.data.currencies);
        } catch (error) {
            console.error('Failed to load metadata:', error);
        }
    }, []);

    useEffect(() => {
        fetchPartners();
        fetchMetadata();
    }, [fetchPartners, fetchMetadata]);

    const resetForm = () => {
        setFormData({
            name: '',
            type: 'Repro',
            is_active: true,
            contact_name: '',
            phone: '',
            email_to: '',
            email_cc: '',
            email_reply_to: '',
            rfq_email: '',
            rfq_email_cc: '',
            notes: '',
            default_currency: 'EUR',
            is_default: false,
            supplier_code: '',
            aliases: '',
            unavailable_periods: []
        });
    };

    const validateEmailList = (raw) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const list = (raw || '').split(',').map(e => e.trim()).filter(Boolean);
        const invalid = list.filter(e => !re.test(e));
        return { list, invalid };
    };

    const handleCreate = async () => {
        if (!formData.name.trim()) {
            toast.error('Partner name is required');
            return;
        }
        if (!formData.email_to.trim()) {
            toast.error('At least one email recipient is required');
            return;
        }
        const { invalid: invalidCc } = validateEmailList(formData.rfq_email_cc);
        if (invalidCc.length > 0) {
            toast.error(`Invalid RFQ CC email(s): ${invalidCc.join(', ')}`);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const payload = {
                ...formData,
                email_to: formData.email_to.split(',').map(e => e.trim()).filter(Boolean),
                email_cc: formData.email_cc ? formData.email_cc.split(',').map(e => e.trim()).filter(Boolean) : [],
                email_reply_to: formData.email_reply_to || null,
                rfq_email: formData.type === 'Supplier' ? ((formData.rfq_email || '').trim() || null) : null,
                rfq_email_cc: formData.type === 'Supplier' ? validateEmailList(formData.rfq_email_cc).list : [],
                supplier_code: (formData.supplier_code || '').trim().toLowerCase() || null,
                aliases: formData.aliases ? formData.aliases.split(',').map(a => a.trim()).filter(Boolean) : [],
                // Only Repro partners carry vacation windows; drop the
                // field entirely for other types so the API doesn't
                // persist empty arrays into Supplier/etc rows.
                unavailable_periods: formData.type === 'Repro'
                    ? (formData.unavailable_periods || []).filter(p => p.start_date && p.end_date)
                    : []
            };
            
            await axios.post(`${API}/api/partners`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Partner created successfully');
            setIsCreateDialogOpen(false);
            resetForm();
            fetchPartners();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create partner');
        }
    };

    const handleEdit = async () => {
        if (!formData.name.trim()) {
            toast.error('Partner name is required');
            return;
        }
        if (!formData.email_to.trim()) {
            toast.error('At least one email recipient is required');
            return;
        }
        const { invalid: invalidCc } = validateEmailList(formData.rfq_email_cc);
        if (invalidCc.length > 0) {
            toast.error(`Invalid RFQ CC email(s): ${invalidCc.join(', ')}`);
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const payload = {
                ...formData,
                email_to: formData.email_to.split(',').map(e => e.trim()).filter(Boolean),
                email_cc: formData.email_cc ? formData.email_cc.split(',').map(e => e.trim()).filter(Boolean) : [],
                email_reply_to: formData.email_reply_to || null,
                rfq_email: formData.type === 'Supplier' ? ((formData.rfq_email || '').trim() || null) : null,
                rfq_email_cc: formData.type === 'Supplier' ? validateEmailList(formData.rfq_email_cc).list : [],
                supplier_code: (formData.supplier_code || '').trim().toLowerCase() || null,
                aliases: formData.aliases ? formData.aliases.split(',').map(a => a.trim()).filter(Boolean) : [],
                unavailable_periods: formData.type === 'Repro'
                    ? (formData.unavailable_periods || []).filter(p => p.start_date && p.end_date)
                    : []
            };
            
            await axios.put(`${API}/api/partners/${selectedPartner.id}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Partner updated successfully');
            setIsEditDialogOpen(false);
            setSelectedPartner(null);
            resetForm();
            fetchPartners();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update partner');
        }
    };

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.delete(`${API}/api/partners/${selectedPartner.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success(response.data.message);
            setIsDeleteDialogOpen(false);
            setSelectedPartner(null);
            fetchPartners();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete partner');
        }
    };

    const handleResendInvitation = async (partnerId) => {
        setResendingInvite(partnerId);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/api/partners/${partnerId}/resend-invitation`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.email_sent) {
                toast.success('Invitation email sent successfully');
            } else {
                toast.error(response.data.error || 'Failed to send invitation');
            }
            fetchPartners();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to resend invitation');
        } finally {
            setResendingInvite(null);
        }
    };

    const openEditDialog = (partner) => {
        setSelectedPartner(partner);
        setFormData({
            name: partner.name,
            type: partner.type,
            is_active: partner.is_active,
            contact_name: partner.contact_name || '',
            phone: partner.phone || '',
            email_to: partner.email_to?.join(', ') || '',
            email_cc: partner.email_cc?.join(', ') || '',
            email_reply_to: partner.email_reply_to || '',
            rfq_email: partner.rfq_email || '',
            rfq_email_cc: partner.rfq_email_cc?.join(', ') || '',
            notes: partner.notes || '',
            default_currency: partner.default_currency || 'EUR',
            is_default: partner.is_default || false,
            supplier_code: partner.supplier_code || '',
            aliases: partner.aliases?.join(', ') || '',
            unavailable_periods: (partner.unavailable_periods || []).map(p => ({ ...p }))
        });
        setIsEditDialogOpen(true);
    };

    const supplierCount = partners.filter(p => p.type === 'Supplier').length;
    const reproCount = partners.filter(p => p.type === 'Repro').length;
    const activeCount = partners.filter(p => p.is_active).length;

    return (
        <div className="min-h-screen bg-background">
            <Header />
            <main className="container mx-auto px-4 py-6 max-w-7xl">
                {/* Page Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-slate-900">Partners</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Production partners, Affinity Programmes and the (upcoming) Affiliate Programme
                    </p>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6" data-testid="partners-tabs">
                        <TabsTrigger value="production" data-testid="partners-tab-production">
                            <Building2 className="h-4 w-4 mr-1.5" />
                            Production Partners
                        </TabsTrigger>
                        <TabsTrigger value="affinity" data-testid="partners-tab-affinity">
                            <Sparkles className="h-4 w-4 mr-1.5" />
                            Affinity Programmes
                        </TabsTrigger>
                        <TabsTrigger value="affiliate" data-testid="partners-tab-affiliate">
                            <Gift className="h-4 w-4 mr-1.5" />
                            Affiliate Programme
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="production" data-testid="partners-tab-content-production">
                        {/* Production Partners — Sony / Supplier / Repro, unchanged.
                            All existing API routes and workflows below stay
                            byte-for-byte identical to the pre-tabs version. */}
                        <div className="flex items-center justify-end mb-4">
                            <Button onClick={() => { resetForm(); setIsCreateDialogOpen(true); }} data-testid="create-partner-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Add Partner
                            </Button>
                        </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-full bg-slate-100">
                                    <Building2 className="h-5 w-5 text-slate-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{partners.length}</p>
                                    <p className="text-xs text-slate-500">Total Partners</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-full bg-blue-100">
                                    <Building2 className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{supplierCount}</p>
                                    <p className="text-xs text-slate-500">Suppliers</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-full bg-purple-100">
                                    <Palette className="h-5 w-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{reproCount}</p>
                                    <p className="text-xs text-slate-500">Repro Partners</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-full bg-emerald-100">
                                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{activeCount}</p>
                                    <p className="text-xs text-slate-500">Active</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Filters */}
                <Card className="mb-6">
                    <CardContent className="pt-4">
                        <div className="flex flex-wrap gap-4 items-center">
                            <div className="flex-1 min-w-[200px]">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                    <Input
                                        placeholder="Search by name or email..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-10"
                                        data-testid="partner-search"
                                    />
                                </div>
                            </div>
                            <Select value={typeFilter} onValueChange={setTypeFilter}>
                                <SelectTrigger className="w-[150px]" data-testid="type-filter">
                                    <SelectValue placeholder="All Types" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Types</SelectItem>
                                    <SelectItem value="Supplier">Suppliers</SelectItem>
                                    <SelectItem value="Repro">Repro</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={activeFilter} onValueChange={setActiveFilter}>
                                <SelectTrigger className="w-[150px]" data-testid="active-filter">
                                    <SelectValue placeholder="All Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Status</SelectItem>
                                    <SelectItem value="active">Active</SelectItem>
                                    <SelectItem value="inactive">Inactive</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardContent>
                </Card>

                {/* Partners Table */}
                <Card>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-background">
                                    <TableHead className="w-[250px]">Partner</TableHead>
                                    <TableHead className="w-[100px]">Type</TableHead>
                                    <TableHead>Contact</TableHead>
                                    <TableHead>Email Recipients</TableHead>
                                    <TableHead className="w-[100px]">Status</TableHead>
                                    <TableHead className="w-[100px] text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                                            Loading partners...
                                        </TableCell>
                                    </TableRow>
                                ) : partners.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                                            No partners found. Create your first partner to get started.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    partners.map((partner) => (
                                        <TableRow key={partner.id} data-testid={`partner-row-${partner.id}`}>
                                            <TableCell>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-medium">{partner.name}</span>
                                                    {partner.is_default && (
                                                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                                            <Star className="h-3 w-3 mr-1" />
                                                            Default
                                                        </Badge>
                                                    )}
                                                    {partner.on_vacation && (
                                                        <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700 border-rose-200" data-testid={`on-vacation-badge-${partner.id}`}>
                                                            On Vacation
                                                        </Badge>
                                                    )}
                                                </div>
                                                {partner.on_vacation && partner.active_period && (
                                                    <p className="text-xs text-rose-700 mt-1">
                                                        {partner.effective_fallback_partner_name
                                                            ? `Handled by ${partner.effective_fallback_partner_name} until ${new Date(partner.active_period.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`
                                                            : `Unavailable until ${new Date(partner.active_period.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} — no fallback set`}
                                                    </p>
                                                )}
                                                {partner.notes && (
                                                    <p className="text-xs text-slate-400 mt-1 truncate max-w-[220px]">{partner.notes}</p>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={partner.type === 'Supplier' ? 'default' : 'secondary'} 
                                                       className={partner.type === 'Repro' ? 'bg-purple-100 text-purple-700' : ''}>
                                                    {partner.type}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="space-y-1">
                                                    {partner.contact_name && (
                                                        <div className="flex items-center gap-1 text-sm">
                                                            <User className="h-3 w-3 text-slate-400" />
                                                            {partner.contact_name}
                                                        </div>
                                                    )}
                                                    {partner.phone && (
                                                        <div className="flex items-center gap-1 text-sm text-slate-500">
                                                            <Phone className="h-3 w-3 text-slate-400" />
                                                            {partner.phone}
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-1 text-sm">
                                                        <Mail className="h-3 w-3 text-slate-400" />
                                                        <span className="truncate max-w-[200px]">{partner.email_to?.join(', ')}</span>
                                                    </div>
                                                    {partner.email_cc?.length > 0 && (
                                                        <div className="text-xs text-slate-400">
                                                            CC: {partner.email_cc.join(', ')}
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="space-y-1">
                                                    <Badge variant={partner.is_active ? 'default' : 'secondary'}
                                                           className={partner.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                                                        {partner.is_active ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                    {partner.linked_user_id ? (
                                                        <div className="text-xs text-emerald-600 flex items-center gap-1">
                                                            <CheckCircle2 className="h-3 w-3" />
                                                            Registered
                                                        </div>
                                                    ) : partner.invitation_sent ? (
                                                        <div className="text-xs text-amber-600 flex items-center gap-1">
                                                            <Mail className="h-3 w-3" />
                                                            Invited
                                                        </div>
                                                    ) : (
                                                        <div className="text-xs text-slate-400">
                                                            No invite
                                                        </div>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    {!partner.linked_user_id && (
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon"
                                                            onClick={() => handleResendInvitation(partner.id)}
                                                            disabled={resendingInvite === partner.id}
                                                            title={partner.invitation_sent ? 'Resend invitation' : 'Send invitation'}
                                                            data-testid={`resend-invite-${partner.id}`}
                                                        >
                                                            {resendingInvite === partner.id ? (
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                            ) : (
                                                                <Send className="h-4 w-4 text-violet-500" />
                                                            )}
                                                        </Button>
                                                    )}
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        onClick={() => openEditDialog(partner)}
                                                        data-testid={`edit-partner-${partner.id}`}
                                                    >
                                                        <Edit2 className="h-4 w-4" />
                                                    </Button>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        onClick={() => { setSelectedPartner(partner); setIsDeleteDialogOpen(true); }}
                                                        className="text-red-500 hover:text-red-700"
                                                        data-testid={`delete-partner-${partner.id}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Create/Edit Partner Dialog */}
                <Dialog open={isCreateDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
                    if (!open) {
                        setIsCreateDialogOpen(false);
                        setIsEditDialogOpen(false);
                        setSelectedPartner(null);
                        resetForm();
                    }
                }}>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{isEditDialogOpen ? 'Edit Partner' : 'Create New Partner'}</DialogTitle>
                            <DialogDescription>
                                {isEditDialogOpen ? 'Update partner details and email routing.' : 'Add a new supplier or repro partner.'}
                            </DialogDescription>
                        </DialogHeader>
                        
                        <div className="grid gap-4 py-4">
                            {/* Basic Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="name">Partner Name *</Label>
                                    <Input
                                        id="name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g., Sony DADC"
                                        data-testid="partner-name-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="type">Type *</Label>
                                    <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                                        <SelectTrigger data-testid="partner-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {partnerTypes.map((t) => (
                                                <SelectItem key={t} value={t}>{t}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Contact Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="contact_name">Contact Name</Label>
                                    <Input
                                        id="contact_name"
                                        value={formData.contact_name}
                                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                                        placeholder="Primary contact person"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone</Label>
                                    <Input
                                        id="phone"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        placeholder="+45 12 34 56 78"
                                    />
                                </div>
                            </div>

                            {/* Email Routing */}
                            <div className="border-t pt-4 mt-2">
                                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                                    <Mail className="h-4 w-4" />
                                    Email Routing
                                </h4>
                                <div className="space-y-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="email_to">To (Recipients) *</Label>
                                        <Input
                                            id="email_to"
                                            value={formData.email_to}
                                            onChange={(e) => setFormData({ ...formData, email_to: e.target.value })}
                                            placeholder="email1@example.com, email2@example.com"
                                            data-testid="partner-email-to-input"
                                        />
                                        <p className="text-xs text-slate-500">Separate multiple emails with commas</p>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="email_cc">CC (Optional)</Label>
                                            <Input
                                                id="email_cc"
                                                value={formData.email_cc}
                                                onChange={(e) => setFormData({ ...formData, email_cc: e.target.value })}
                                                placeholder="cc@example.com"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="email_reply_to">Reply-To (Optional)</Label>
                                            <Input
                                                id="email_reply_to"
                                                value={formData.email_reply_to}
                                                onChange={(e) => setFormData({ ...formData, email_reply_to: e.target.value })}
                                                placeholder="replies@example.com"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Type-specific fields */}
                            {formData.type === 'Supplier' && (
                                <div className="border-t pt-4 mt-2">
                                    <h4 className="text-sm font-medium mb-3">Supplier Settings</h4>
                                    <div className="space-y-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="rfq_email">RFQ Email (Optional)</Label>
                                            <Input
                                                id="rfq_email"
                                                value={formData.rfq_email}
                                                onChange={(e) => setFormData({ ...formData, rfq_email: e.target.value })}
                                                placeholder="quotations@supplier.com"
                                                data-testid="partner-rfq-email-input"
                                            />
                                            <p className="text-xs text-slate-500">
                                                Dedicated inbox for Request-For-Quotation emails. Falls back to the first <strong>To (Recipients)</strong> address when empty.
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="rfq_email_cc">RFQ CC Email(s) (Optional)</Label>
                                            <Input
                                                id="rfq_email_cc"
                                                value={formData.rfq_email_cc}
                                                onChange={(e) => setFormData({ ...formData, rfq_email_cc: e.target.value })}
                                                placeholder="cc1@supplier.com, cc2@example.com"
                                                data-testid="partner-rfq-cc-input"
                                            />
                                            <p className="text-xs text-slate-500">
                                                Comma-separated. Pre-filled as CC on the Send RFQ dialog (editable per send). Falls back to the general <strong>CC</strong> when empty.
                                            </p>
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="default_currency">Default Currency</Label>
                                            <Select value={formData.default_currency} onValueChange={(v) => setFormData({ ...formData, default_currency: v })}>
                                                <SelectTrigger className="w-[150px]">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {currencies.map((c) => (
                                                        <SelectItem key={c} value={c}>{c}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Supplier-resolution keys (Suppliers only). Decouples
                                email routing from the editable display name so
                                e.g. "Sony DADC" on a PO still resolves to a
                                partner named "Sony DADC Client Service". */}
                            {formData.type === 'Supplier' && (
                                <div className="space-y-2 border-t pt-3">
                                    <div className="text-xs font-semibold text-slate-700 mb-1">Supplier matching</div>
                                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                        <div className="space-y-1">
                                            <Label htmlFor="supplier_code">Supplier code (stable id)</Label>
                                            <Input
                                                id="supplier_code"
                                                value={formData.supplier_code}
                                                onChange={(e) => setFormData({ ...formData, supplier_code: e.target.value })}
                                                placeholder="e.g. sony_dadc"
                                                data-testid="partner-supplier-code-input"
                                            />
                                            <p className="text-[10px] text-slate-500">
                                                Canonical id used for email routing. Lowercase + underscores. Leave blank to fall back to alias/name matching.
                                            </p>
                                        </div>
                                        <div className="space-y-1">
                                            <Label htmlFor="aliases">Aliases (comma-separated)</Label>
                                            <Input
                                                id="aliases"
                                                value={formData.aliases}
                                                onChange={(e) => setFormData({ ...formData, aliases: e.target.value })}
                                                placeholder="e.g. Sony DADC, Sony Music Manufacturing"
                                                data-testid="partner-aliases-input"
                                            />
                                            <p className="text-[10px] text-slate-500">
                                                Alternative display names that should also match this partner when entered on a PO.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Notes */}
                            <div className="space-y-2">
                                <Label htmlFor="notes">Notes (Admin only)</Label>
                                <Textarea
                                    id="notes"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    placeholder="Internal notes about this partner..."
                                    rows={2}
                                />
                            </div>

                            {/* Status and Default */}
                            <div className="border-t pt-4 mt-2 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <Switch
                                        id="is_active"
                                        checked={formData.is_active}
                                        onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                                    />
                                    <Label htmlFor="is_active">Active</Label>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Switch
                                        id="is_default"
                                        checked={formData.is_default}
                                        onCheckedChange={(v) => setFormData({ ...formData, is_default: v })}
                                    />
                                    <Label htmlFor="is_default" className="flex items-center gap-1">
                                        <Star className="h-4 w-4 text-amber-500" />
                                        Set as Default {formData.type}
                                    </Label>
                                </div>
                            </div>
                        </div>

                        {/* Vacation windows — Repro Partners only. Multiple
                            future periods supported; each routes to a
                            different fallback while active. */}
                        {formData.type === 'Repro' && (
                            <div className="space-y-2 border-t pt-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <Label className="text-sm font-medium">Unavailable periods (vacation)</Label>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            While a period is active, new repro jobs and emails auto-route to the fallback partner. Switches back automatically when the period ends.
                                        </p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setFormData({
                                            ...formData,
                                            unavailable_periods: [
                                                ...(formData.unavailable_periods || []),
                                                { start_date: '', end_date: '', fallback_partner_id: '', note: '' },
                                            ],
                                        })}
                                        data-testid="add-vacation-period-btn"
                                    >
                                        + Add period
                                    </Button>
                                </div>
                                {(formData.unavailable_periods || []).length === 0 ? (
                                    <p className="text-xs text-slate-400 italic py-2">No vacation periods configured.</p>
                                ) : (
                                    (formData.unavailable_periods || []).map((period, idx) => {
                                        const fallbackChoices = partners.filter(p =>
                                            p.type === 'Repro' && p.is_active && p.id !== (selectedPartner?.id)
                                        );
                                        const update = (field, value) => {
                                            const next = [...formData.unavailable_periods];
                                            next[idx] = { ...next[idx], [field]: value };
                                            setFormData({ ...formData, unavailable_periods: next });
                                        };
                                        const remove = () => {
                                            const next = formData.unavailable_periods.filter((_, i) => i !== idx);
                                            setFormData({ ...formData, unavailable_periods: next });
                                        };
                                        return (
                                            <div key={period.id || idx} className="grid grid-cols-12 gap-2 items-end border rounded-md p-2 bg-slate-50" data-testid={`vacation-period-row-${idx}`}>
                                                <div className="col-span-3">
                                                    <Label className="text-[11px] text-slate-500">Unavailable from</Label>
                                                    <input
                                                        type="date"
                                                        value={period.start_date || ''}
                                                        onChange={(e) => update('start_date', e.target.value)}
                                                        className="w-full px-2 py-1 text-sm border rounded"
                                                        data-testid={`vacation-start-${idx}`}
                                                    />
                                                </div>
                                                <div className="col-span-3">
                                                    <Label className="text-[11px] text-slate-500">Unavailable until</Label>
                                                    <input
                                                        type="date"
                                                        value={period.end_date || ''}
                                                        onChange={(e) => update('end_date', e.target.value)}
                                                        className="w-full px-2 py-1 text-sm border rounded"
                                                        data-testid={`vacation-end-${idx}`}
                                                    />
                                                </div>
                                                <div className="col-span-3">
                                                    <Label className="text-[11px] text-slate-500">Fallback partner</Label>
                                                    <select
                                                        value={period.fallback_partner_id || ''}
                                                        onChange={(e) => update('fallback_partner_id', e.target.value || null)}
                                                        className="w-full px-2 py-1 text-sm border rounded bg-white"
                                                        data-testid={`vacation-fallback-${idx}`}
                                                    >
                                                        <option value="">— Select —</option>
                                                        {fallbackChoices.map(p => (
                                                            <option key={p.id} value={p.id}>{p.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-span-2">
                                                    <Label className="text-[11px] text-slate-500">Note</Label>
                                                    <input
                                                        type="text"
                                                        value={period.note || ''}
                                                        onChange={(e) => update('note', e.target.value)}
                                                        className="w-full px-2 py-1 text-sm border rounded"
                                                        placeholder="Summer holiday"
                                                    />
                                                </div>
                                                <div className="col-span-1 text-right">
                                                    <button
                                                        type="button"
                                                        onClick={remove}
                                                        className="text-rose-600 hover:text-rose-800 text-xs"
                                                        data-testid={`remove-vacation-${idx}`}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}

                        <DialogFooter>
                            <Button variant="outline" onClick={() => {
                                setIsCreateDialogOpen(false);
                                setIsEditDialogOpen(false);
                                resetForm();
                            }}>
                                Cancel
                            </Button>
                            <Button onClick={isEditDialogOpen ? handleEdit : handleCreate} data-testid="save-partner-btn">
                                {isEditDialogOpen ? 'Save Changes' : 'Create Partner'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-red-600">
                                <AlertCircle className="h-5 w-5" />
                                Delete Partner
                            </DialogTitle>
                            <DialogDescription>
                                Are you sure you want to delete <strong>{selectedPartner?.name}</strong>?
                                {selectedPartner?.is_default && (
                                    <span className="block mt-2 text-amber-600">
                                        ⚠️ This is the default {selectedPartner.type} partner. You may want to set another partner as default first.
                                    </span>
                                )}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
                                Delete
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
                    </TabsContent>

                    <TabsContent value="affinity" data-testid="partners-tab-content-affinity">
                        {/* Affinity Programmes — moved from System Settings →
                            Partner Registration (2026-02). The underlying
                            DB collection, /register/{slug} URLs and every
                            resolver / discount / currency code path is
                            unchanged; only the label is Affinity. */}
                        <PartnerProgrammesPanel />
                    </TabsContent>

                    <TabsContent value="affiliate" data-testid="partners-tab-content-affiliate">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Gift className="h-5 w-5 text-emerald-600" />
                                    Affiliate Programme
                                </CardTitle>
                                <CardDescription>
                                    Track partners who refer new customers and share revenue
                                    (roadmap — not live yet).
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center bg-slate-50/60">
                                    <p className="text-sm text-slate-600">
                                        Coming soon. This tab will manage referral partners,
                                        unique referral links, commission tiers and payouts —
                                        distinct from the Affinity Programmes shown to end
                                        customers on /register/{'{'}slug{'}'}.
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}
